/** UI-free adapter of LoonaPay's RUB topup flow. Requires an authenticated
 * axios-compatible client with baseURL ending in /api. No keys belong here.
 * Styling, rendering, routing and timers remain in the receiving project. */
export const TERMINAL = new Set(["credited", "expired", "canceled", "rejected", "failed"]);
export const UNCERTAIN = new Set(["creating", "creation_unknown"]);

const newKey = () => globalThis.crypto?.randomUUID?.()
  || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const failed = (code) => Object.assign(new Error(code), { code });

export function actionForInvoice(invoice, now = Date.now() / 1000) {
  const status = invoice?.status;
  const verification = ["pending", "kyc_pending"].includes(status);
  const url = verification ? invoice?.verification_url
    : status === "payin_pending" ? invoice?.payment_url : null;
  const expires = verification ? invoice?.verification_expires_at : invoice?.invoice_expires_at;
  if (!url) return null;
  let parsed;
  try { parsed = new URL(url); } catch { return null; }
  if (parsed.protocol !== "https:" || parsed.username || parsed.password) return null;
  // Backend exposes timestamps in seconds. Do not parse a naive provider date.
  const deadline = Number(expires);
  return { kind: verification ? "verification" : "payment", url,
    expired: Number.isFinite(deadline) && deadline > 0 && deadline <= now };
}

export function validateCustomer(draft) {
  const customer = draft.customer || {};
  const errors = {};
  for (const key of ["first_name", "second_name"]) {
    if (!/^[А-Яа-яЁё]+(?:[ -][А-Яа-яЁё]+)*$/.test(String(customer[key] || "").trim()) || String(customer[key]).trim().length > 100) errors[key] = "invalid_name";
  }
  const middle = String(customer.middle_name || "").trim();
  if (middle && (!/^[А-Яа-яЁё]+(?:[ -][А-Яа-яЁё]+)*$/.test(middle) || middle.length > 100)) errors.middle_name = "invalid_name";
  const phone = String(customer.phone || "").replace(/[\s()-]/g, "");
  if (!/^\+?\d{10,15}$/.test(phone)) errors.phone = "invalid_phone";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(draft.user_email || "").trim()) || String(draft.user_email).trim().length > 254) errors.user_email = "invalid_email";
  return errors;
}

function normalizedDraft(draft, telegram) {
  const errors = validateCustomer(draft);
  if (Object.keys(errors).length) throw Object.assign(failed("invalid_customer"), { fields: errors });
  const hasRub = draft.rub_amount != null;
  const hasCrypto = draft.crypto_amount != null;
  const amount = Number(hasRub ? draft.rub_amount : draft.crypto_amount);
  if (hasRub === hasCrypto || !Number.isFinite(amount) || amount <= 0) throw failed("invalid_amount");
  return { target_currency: "USDT", [hasRub ? "rub_amount" : "crypto_amount"]: amount,
    user_email: draft.user_email.trim(),
    customer: { first_name: draft.customer.first_name.trim(), second_name: draft.customer.second_name.trim(),
      middle_name: String(draft.customer.middle_name || "").trim(),
      phone: draft.customer.phone.replace(/[\s()-]/g, "") },
    client_context: telegram ? "telegram" : "web" };
}

export class DragonTransferFlow {
  constructor(api, { telegram = false, makeKey = newKey } = {}) {
    this.api = api;
    this.telegram = telegram;
    this.makeKey = makeKey;
    this.invoice = null;
    this.config = null;
    this.attempt = null; // PII stays in memory, never localStorage/logs.
    this.pendingCreate = null;
    this.pendingRefresh = null;
  }

  async getConfig() {
    this.config = (await this.api.get("/rub-topup/config")).data;
    return this.config;
  }

  async quote({ rub_amount, crypto_amount }) {
    const hasRub = rub_amount != null;
    const hasCrypto = crypto_amount != null;
    const amount = Number(hasRub ? rub_amount : crypto_amount);
    if (hasRub === hasCrypto || !Number.isFinite(amount) || amount <= 0) throw failed("invalid_amount");
    return (await this.api.post("/rub-topup/quote", { target_currency: "USDT",
      [hasRub ? "rub_amount" : "crypto_amount"]: amount })).data;
  }

  async restore(orderId = null) {
    const response = await this.api.get(orderId
      ? `/rub-topup/status/${encodeURIComponent(orderId)}` : "/rub-topup/active");
    const rows = orderId ? [response.data] : response.data;
    if (!Array.isArray(rows)) throw failed("invalid_response");
    this.invoice = rows.find(row => UNCERTAIN.has(row?.status)) || rows[0] || null;
    return this.invoice;
  }

  create(draft) {
    // This promise gate also handles rapid double taps before UI rerenders.
    if (this.pendingCreate) return this.pendingCreate;
    let body;
    try { body = normalizedDraft(draft, this.telegram); } catch (error) { return Promise.reject(error); }
    if (this.attempt && JSON.stringify(this.attempt.draft) !== JSON.stringify(body)) {
      return Promise.reject(failed("resolve_previous_attempt_first"));
    }
    if (this.invoice) return Promise.resolve(this.invoice);
    if (!this.attempt) this.attempt = { draft: body, body: { ...body, idempotency_key: this.makeKey() } };
    this.pendingCreate = this._sendCreate().finally(() => { this.pendingCreate = null; });
    return this.pendingCreate;
  }

  async _sendCreate() {
    try {
      this.invoice = (await this.api.post("/rub-topup/invoice", this.attempt.body)).data;
      return this.invoice;
    } catch (error) {
      const status = error.response?.status;
      const uncertain = !status || status >= 500 || status === 408 || status === 409;
      if (uncertain) {
        // A lost response must not create a new remote order on a fresh key.
        try {
          const saved = await this.restore();
          if (saved) return saved;
        } catch { /* keep exact request body/key for an explicit retry */ }
      } else {
        this.attempt = null;
      }
      throw error;
    }
  }

  refresh() {
    if (this.pendingRefresh) return this.pendingRefresh;
    if (!this.invoice?.order_id) return Promise.resolve(null);
    const orderId = this.invoice.order_id;
    this.pendingRefresh = this.api.get(`/rub-topup/status/${encodeURIComponent(orderId)}`)
      .then(response => {
        if (this.invoice?.order_id !== orderId) return this.invoice;
        this.invoice = response.data; return this.invoice;
      })
      .finally(() => { this.pendingRefresh = null; });
    return this.pendingRefresh;
  }

  async dismiss() {
    if (this.pendingCreate || (!this.invoice && this.attempt)) throw failed("resolve_previous_attempt_first");
    if (!this.invoice?.order_id) return null;
    // /cancel acknowledges dismissal with status only; retain the owned order,
    // currency and provider fields while an uncertain create remains blocking.
    const saved = { ...this.invoice, ...(await this.api.post(`/rub-topup/${encodeURIComponent(this.invoice.order_id)}/cancel`)).data };
    if (UNCERTAIN.has(saved.status)) {
      this.invoice = saved;
      return saved;
    }
    this.invoice = null;
    this.attempt = null;
    return saved; // Dismissal is not a refund, financial cancellation or stop of reconciliation.
  }

  async close() {
    if (this.pendingCreate || (!this.invoice && this.attempt)) throw failed("resolve_previous_attempt_first");
    if (!this.invoice?.order_id) return null;
    const saved = (await this.api.post(`/rub-topup/${encodeURIComponent(this.invoice.order_id)}/close`)).data;
    if (!saved?.user_closed_at) throw failed("close_not_confirmed");
    this.invoice = null;
    this.attempt = null;
    return saved;
  }
}

export function openExternalAction(action, webApp = globalThis.Telegram?.WebApp) {
  if (!action || action.expired) return false;
  let parsed;
  try { parsed = new URL(action.url); } catch { return false; }
  if (parsed.protocol !== "https:" || parsed.username || parsed.password) return false;
  if (webApp?.openLink) {
    try { webApp.openLink(action.url); return true; } catch { /* regular browser fallback */ }
  }
  // Same-window navigation works even when the browser blocks a popup after
  // an asynchronous create response. The provider return restores this order.
  if (globalThis.location?.assign) {
    globalThis.location.assign(action.url);
    return true;
  }
  return Boolean(globalThis.open?.(action.url, "_blank", "noopener,noreferrer"));
}
